import {
  Authenticated,
  useTranslate,
  useCreate,
  useList,
  useInvalidate,
  useNavigation,
} from "@refinedev/core";
import React, { useState } from "react";
import { Flex, Row, Col } from "antd";
import { NotebookIcon } from "@phosphor-icons/react";
import {
  Button,
  Card,
  EmptyState,
  GRADIENT_KEYS,
  Input,
  PlaybookCard,
  Spinner,
} from "@libertify/ui";
import { useSession } from "hooks/useSession";
import { SavedSettingsResponse } from "pages/media/types";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { MenuCTA } from "components/MenuCTA";
import { PlaybookHint } from "./components/PlaybookHint";
import { PageLayout } from "components/PageLayout";
import { useAntTheme } from "hooks/useAntTheme";

export const PlaybooksPage: React.FC = () => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { edit } = useNavigation();
  const [isCreating, setIsCreating] = useState(false);
  const [newPlaybookName, setNewPlaybookName] = useState("");
  const { currentOrganization } = useSession();
  const invalidate = useInvalidate();
  const { trackEvent } = useAnalytics();
  const organizationId = currentOrganization?.id;

  const { data: playbooksData, isLoading: playbooksLoading } =
    useList<SavedSettingsResponse>({
      resource: `media/${organizationId}/saved-settings`,
      queryOptions: {
        enabled: !!organizationId,
      },
    });

  const createMutation = useCreate();

  const playbooks = playbooksData?.data || [];

  const createPlaybook = async () => {
    if (!newPlaybookName.trim()) return;

    try {
      const response = await createMutation.mutateAsync({
        resource: `media/${organizationId}/saved-settings`,
        values: {
          name: newPlaybookName.trim(),
        },
        successNotification: {
          message: t("pages.playbooks.playbookCreated"),
          type: "success",
          description: t("pages.playbooks.success"),
        },
      });

      const newPlaybookId = (response.data as SavedSettingsResponse).id;

      trackEvent(AnalyticsEvents.SettingsPlaybookSave, {
        playbook_id: newPlaybookId,
        playbook_name: newPlaybookName.trim(),
      });

      setNewPlaybookName("");
      setIsCreating(false);

      await invalidate({
        resource: `media/${organizationId}/saved-settings`,
        invalidates: ["list"],
      });

      edit("playbooks", newPlaybookId);
    } catch (error) {
      console.error("Create failed:", error);
    }
  };

  const handleCreatePlaybook = () => {
    createPlaybook().catch((error) => {
      console.error("Create failed:", error);
    });
  };

  if (!organizationId || playbooksLoading) {
    return (
      <Flex justify="center" align="center" style={{ height: "100vh" }}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Authenticated key="playbooks">
      <PageLayout
        title={t("pages.playbooks.title")}
        description={<PlaybookHint />}
        breadcrumbLabel={t("src.App.playbooks")}
        actions={
          <Button
            variant="outlined"
            size="l"
            leftIcon={<NotebookIcon size={16} />}
            onClick={() => {
              trackEvent(AnalyticsEvents.SettingsPlaybookCreate, {});
              setIsCreating(true);
            }}
          >
            {t("pages.playbooks.createPlaybook")}
          </Button>
        }
      >
        <Flex vertical gap={theme.marginLG}>
          {isCreating && (
            <Card>
              <Flex gap={12} align="center">
                <Input
                  placeholder={t("pages.playbooks.playbookNamePlaceholder")}
                  value={newPlaybookName}
                  onChange={(e) => setNewPlaybookName(e.target.value)}
                  onPressEnter={handleCreatePlaybook}
                  style={{ maxWidth: 300 }}
                />
                <Button
                  variant="primary"
                  onClick={handleCreatePlaybook}
                  loading={createMutation.isPending}
                  disabled={!newPlaybookName.trim()}
                >
                  {t("buttons.save")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setIsCreating(false)}
                >
                  {t("buttons.cancel")}
                </Button>
              </Flex>
            </Card>
          )}

          {playbooks.length === 0 ? (
            <Card>
              <EmptyState
                title={t("pages.playbooks.noPlaybooks")}
                action={{
                  label: t("pages.playbooks.createFirstPlaybook"),
                  onClick: () => setIsCreating(true),
                }}
              />
            </Card>
          ) : (
            <Row gutter={[16, 16]} align="stretch">
              {playbooks.map((playbook, index) => (
                <Col xs={24} sm={12} md={6} key={playbook.id}>
                  <PlaybookCard
                    owner={
                      currentOrganization.name
                        ? t("pages.playbooks.by", {
                            organizationName: currentOrganization.name,
                          })
                        : undefined
                    }
                    title={playbook.name}
                    subtitle={playbook.description ?? undefined}
                    gradient={GRADIENT_KEYS[index % GRADIENT_KEYS.length]}
                    onClick={() => edit("playbooks", playbook.id)}
                    menu={
                      <MenuCTA
                        onEditClick={() => edit("playbooks", playbook.id)}
                        recordId={playbook.id}
                        resource={`media/${organizationId}/saved-settings`}
                      />
                    }
                  />
                </Col>
              ))}
            </Row>
          )}
        </Flex>
      </PageLayout>
    </Authenticated>
  );
};
